import './MainSelector.css'

import { Autocomplete, TextField, Typography, Box, CircularProgress } from "@mui/material";
import { useQuery } from '@tanstack/react-query';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { fetchPersonByGrampsId, searchPeopleByName, restPersonName } from '../api/grampsRest';
import { getActiveGrampsProfileId, readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import { TabsLayout } from '../tab/TabsLayout';
import { Landmarks } from './Landmarks';
import { useViewRoute } from '../viewRoute';

type PersonOption = { id: string; label: string };

// Only queried once typing settles, so every keystroke doesn't fire a request.
const SEARCH_DEBOUNCE_MS = 250;
// Below this, results are too broad to be useful and the tree is left unqueried.
const MIN_SEARCH_LENGTH = 2;

export function MainSelector() {
    const { mainId, sideItemId, navigateTo, treeName } = useViewRoute()
    // Reactive so a token refreshed after a 401 (silent or via the login dialog)
    // re-renders this component with the new token, letting react-query's
    // `invalidateQueries()` retry pick it up instead of replaying the stale one.
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const profileId = getActiveGrampsProfileId();

    // The only fetch needed before first paint: resolves the currently selected
    // person's label so the field can show a value without loading the whole tree.
    const mainPersonQuery = useQuery({
        queryKey: ['person', 'main-selector', treeName, profileId, mainId],
        queryFn: () => mainId ? fetchPersonByGrampsId(mainId, token) : Promise.resolve(undefined),
        enabled: !!mainId,
    });
    const selectedValue = useMemo<PersonOption | null>(() => {
        const person = mainPersonQuery.data;
        return person?.gramps_id ? { id: person.gramps_id, label: restPersonName(person) } : null;
    }, [mainPersonQuery.data]);

    const [inputValue, setInputValue] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedTerm(inputValue), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [inputValue]);

    const searchQuery = useQuery({
        queryKey: ['people', 'search', treeName, profileId, debouncedTerm],
        queryFn: () => searchPeopleByName(debouncedTerm, token),
        enabled: debouncedTerm.trim().length >= MIN_SEARCH_LENGTH,
    });
    const searchResults = useMemo<PersonOption[]>(() => (searchQuery.data ?? [])
        .filter((person) => person.gramps_id)
        .map((person) => ({ id: person.gramps_id!, label: restPersonName(person) }))
        .sort((first, second) => first.label.localeCompare(second.label, 'fr')),
    [searchQuery.data]);

    // The selected option must stay visible even once it scrolls out of the latest
    // search results (or before any search has run), otherwise Autocomplete would
    // render it as if it weren't a valid option.
    const options = useMemo(() => selectedValue && !searchResults.some((option) => option.id === selectedValue.id)
        ? [selectedValue, ...searchResults]
        : searchResults,
    [searchResults, selectedValue]);

    const handleChange = useCallback((_event: SyntheticEvent, newValue: PersonOption | null) => {
        if (newValue) {
            // Choosing the first main person keeps open the drawer of whoever
            // was being looked at; choosing another one recentres on them.
            const nextSideItemId = mainId ? newValue.id : sideItemId ?? newValue.id;
            window.setTimeout(() => navigateTo(newValue.id, nextSideItemId, { clearSearch: true }), 0);
        }
    }, [mainId, navigateTo, sideItemId])

    return (<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 600 }, // constrain to mobile width (600px) on larger screens
            flexDirection: { xs: 'column', sm: 'row' }, // stack on mobile
            alignItems: 'center'
        }}>
            <Typography id="track-false-slider" gutterBottom>
                Choisir une personne sur laquelle centrer la vue généalogique (vous-même p.ex.)
            </Typography>
            <Autocomplete
                fullWidth
                disablePortal
                options={options}
                filterOptions={(x) => x}
                value={selectedValue}
                inputValue={inputValue}
                onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
                loading={searchQuery.isFetching}
                noOptionsText={inputValue.trim().length < MIN_SEARCH_LENGTH ? 'Tapez au moins 2 caractères' : 'Aucun résultat'}
                renderInput={(params) => <TextField {...params} label="Personne" slotProps={{
                    ...params.slotProps,
                    input: {
                        ...params.slotProps.input,
                        endAdornment: <>{searchQuery.isFetching && <CircularProgress color="inherit" size={16} />}{params.slotProps.input.endAdornment}</>,
                    },
                }} />}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={handleChange}
                blurOnSelect={true}
                size="small"
                sx={{ minWidth: 0 }}
            />
        </Box>

        {/* Without a main person yet, somewhere to start: not everyone
            knows whom to look for. */}
        {!mainId && <Landmarks treeName={treeName} profileId={profileId} token={token}
            onChoose={(id) => navigateTo(id, sideItemId ?? id, { clearSearch: true })} />}

        { selectedValue && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <TabsLayout />
            </Box>
        ) }
    </Box>)
}
